import React, { useEffect, useState } from "react";
import {
  Calendar,
  Wallet,
  Globe,
  ArrowRight,
  Filter,
  ChevronRight,
  XCircle,
  ChevronLeft,
} from "lucide-react";
import { getDailyCollection } from "../../services/storeOwnerOrderServices";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { ArrowLeft } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { formatCurrencyINR } from "../../utils/function";
import { useTranslation } from "react-i18next";
import { notify } from "../../components/Common/Alerts";
import { useSelector } from "react-redux";

const DailyCollection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getToday = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  let auth = useSelector((x) => x.auth.value);

  const [collectionDataList, setCollectionDataList] = useState([]);
  const [filters, setFilters] = useState({
    from_date: getToday(),
    to_date: getToday(),
    store_id: auth.active_store_id,
  });
  const [tempFilters, setTempFilters] = useState({
    from_date: getToday(),
    to_date: getToday(),
  });

  const isFilterApplied =
    filters.from_date !== getToday() || filters.to_date !== getToday();

  const handleSearch = () => {
    setFilters(tempFilters);
  };

  const handleReset = () => {
    const today = getToday();

    setTempFilters({
      from_date: today,
      to_date: today,
    });

    setFilters({
      from_date: today,
      to_date: today,
    });
  };

  useEffect(() => {
    const data = {
      ...filters,
    };

    getDailyCollection(data)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setCollectionDataList(decrypted.data);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, [filters]);

  const handleFromDateChange = (e) => {
    const value = e.target.value;

    if (value && Number(value.split("-")[0]) < 2000) {
      return;
    }

    setTempFilters((prev) => ({
      ...prev,
      from_date: value,
    }));
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;

    const year = Number(value.split("-")[0]);

    if (year < 2000) return;

    if (tempFilters.from_date && value < tempFilters.from_date) {
      return;
    }

    setTempFilters((prev) => ({
      ...prev,
      to_date: value,
    }));
  };

  return (
    <div className="bg-[#F8F9FB] dark:bg-slate-950 pb-20 font-sans antialiased transition-colors duration-300">
      <div
        className="bg-[#E67E22] dark:bg-orange-600 p-4 flex items-center gap-3 text-white shadow-md cursor-pointer hidden md:flex md:mb-8 sticky top-0 z-30 shadow-sm"
        onClick={() => navigate("/dashboard")}
      >
        <ChevronLeft size={24} />
        <h1 className="text-lg font-bold">
          {t("dailyCollection")} ({t("revenueAnalytics")})
        </h1>
      </div>

      <main className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="bg-white dark:bg-slate-900 rounded-3xl md:rounded-[2.5rem] p-4 md:p-6 shadow-xl shadow-gray-200/40 dark:shadow-none border border-gray-100 dark:border-slate-800 mb-6 md:mb-10">
          <div className="flex-1 flex flex-col sm:flex-row items-center gap-3 bg-gray-50 dark:bg-slate-800/50 p-3 rounded-2xl border border-gray-100 dark:border-slate-800">
            <div className="flex items-center gap-2 w-full sm:w-auto flex-1">
              <input
                type="date"
                value={tempFilters.from_date || ""}
                min="2000-01-01"
                max={tempFilters.to_date || "2100-12-31"}
                onChange={handleFromDateChange}
                className="w-full bg-transparent text-sm font-bold text-gray-700 dark:text-gray-200 focus:outline-none"
              />
              <ArrowRight size={14} className="text-gray-300 shrink-0" />
              <input
                type="date"
                name="to_date"
                value={tempFilters.to_date || ""}
                min={tempFilters.from_date || undefined}
                max="2100-12-31"
                onChange={handleToDateChange}
                className="w-full bg-transparent text-sm font-bold text-gray-700 dark:text-gray-200 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-200 dark:border-slate-700">
              <button
                type="button"
                onClick={handleSearch}
                className="flex-1 sm:flex-none flex justify-center bg-orange-500 p-3 rounded-xl text-white shadow-lg shadow-orange-200 dark:shadow-none"
              >
                <Filter size={18} />
              </button>

              {isFilterApplied && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-900/30 rounded-[1.2rem] text-sm font-black text-red-600 dark:text-red-400 transition-all hover:bg-red-100 dark:hover:bg-red-500/20 active:scale-95 animate-in fade-in zoom-in duration-200"
                >
                  <XCircle size={16} />
                  <span>{t("clear")}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-8 md:space-y-12">
          {collectionDataList?.length > 0 ? (
            collectionDataList.map((day, idx) => (
              <div key={idx} className="relative">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="p-2 bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-100 dark:border-slate-800">
                    <Calendar size={16} className="text-orange-500" />
                  </div>
                  <span className="text-xs md:text-sm font-black uppercase tracking-widest text-gray-500">
                    {new Date(day.date).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-gray-200 dark:from-slate-800 to-transparent"></div>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden">
                  <div className="grid grid-cols-3 bg-gray-50/50 dark:bg-slate-800/20 border-b border-gray-50 dark:border-slate-800">
                    <div className="py-4 md:py-6 text-center border-r border-gray-100 dark:border-slate-800">
                      <p className="text-[8px] md:text-[10px] font-black text-gray-400 uppercase mb-1">
                        {t("total")}
                      </p>
                      <p className="text-sm md:text-2xl font-black text-gray-900 dark:text-white leading-none">
                        {formatCurrencyINR(day.total_collection)}
                      </p>
                    </div>
                    <div className="py-4 md:py-6 text-center border-r border-gray-100 dark:border-slate-800">
                      <p className="text-[8px] md:text-[10px] font-black text-orange-500 uppercase mb-1">
                        {t("cash")}
                      </p>
                      <p className="text-sm md:text-2xl font-black text-orange-600 leading-none">
                        {formatCurrencyINR(day.cash)}
                      </p>
                    </div>
                    <div className="py-4 md:py-6 text-center">
                      <p className="text-[8px] md:text-[10px] font-black text-blue-500 uppercase mb-1">
                        {t("online")}
                      </p>
                      <p className="text-sm md:text-2xl font-black text-blue-600 leading-none">
                        {formatCurrencyINR(day.online)}
                      </p>
                    </div>
                  </div>

                  <div className="p-3 md:p-6 space-y-2 md:space-y-3">
                    {day.transactions.map((t, tIdx) => (
                      <div
                        key={tIdx}
                        className="flex items-center justify-between p-3 md:p-4 rounded-2xl bg-white dark:bg-slate-900 border border-gray-50 dark:border-slate-800 active:bg-gray-50 dark:active:bg-slate-800 transition-all group"
                      >
                        <div className="flex items-center gap-3 md:gap-4">
                          <div
                            className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0 ${
                              t.payment_method === "Cash"
                                ? "bg-orange-50 dark:bg-orange-950/30 text-orange-600"
                                : "bg-blue-50 dark:bg-blue-950/30 text-blue-600"
                            }`}
                          >
                            {t.payment_method === "Cash" ? (
                              <Wallet size={18} />
                            ) : (
                              <Globe size={18} />
                            )}
                          </div>

                          <div className="flex flex-col min-w-0">
                            <p className="text-[11px] md:text-sm font-black text-gray-900 dark:text-white uppercase truncate tracking-tight">
                              {t.title.split("•")[0]?.trim()}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[9px] md:text-[11px] font-bold text-gray-400">
                                {t.title.split("•")[1]?.trim()}
                              </span>
                              <span className="w-1 h-1 rounded-full bg-gray-200"></span>
                              <span
                                className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest ${t.payment_method === "Cash" ? "text-orange-500" : "text-blue-500"}`}
                              >
                                {t.payment_method}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 pl-2">
                          <p className="text-xs md:text-base font-black text-gray-900 dark:text-white">
                            {formatCurrencyINR(t.amount)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-400 py-20 bg-white dark:bg-slate-900 rounded-3xl border border-dashed border-gray-200">
              <p className="text-xs font-black uppercase tracking-widest">
                {t("noDailyCollectionFound")}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DailyCollection;
